// RMI-2026-591 (react-refresh/only-export-components): split out of
// ThemeContext.jsx, which now exports only the ThemeProvider component.
export const themes = {
  DARK: {
    // 1. Layered Backgrounds (RMI-2026-423: brand gradient, blue-led, replacing
    // the flat neutral grey from RMI-2026-342 — every value below is a darkened
    // derivative of the three official brand hex codes (Blue #5899CD, Purple
    // #824AF8, Coral #F37664) and the documented 135deg brand gradient, per
    // the actual Tiger Technology & Logistics Brand Guideline PDF. Each
    // surface uses a different slice of the same blue→purple→coral
    // relationship: floor gets the full three-color sweep (the whole brand
    // identity), navbar/sidebar/input stay in the blue family (2-stop,
    // low-contrast — input deliberately narrowest for text legibility),
    // card leads blue into purple. NOTE: any consumer of these tokens MUST
    // use the `background` shorthand, never `background-color` — a CSS
    // custom property holding a gradient silently fails on `background-color`
    // (fixed at all known usage sites in the same commit as this change).
    '--bg-main': 'linear-gradient(135deg, #0F3957 0%, #250F57 55%, #57190F 100%)',
    '--bg-sidebar': 'linear-gradient(90deg, #124468 0%, #1B557E 100%)',
    '--bg-card': 'linear-gradient(135deg, #155079 0%, #331579 100%)',
    '--bg-navbar': 'linear-gradient(90deg, #1B557E 0%, #331579 100%)', // RMI-2026-486: purple-forward — eases into the same purple used for active-tab icons/IRIS instead of duplicating --bg-sidebar's flat blue

    // 2. Interactive Elements
    '--bg-input': 'linear-gradient(135deg, #124468 0%, #155079 100%)', // Form Inputs — narrowest, lowest-contrast slice
    '--bg-hover': '#276B9B',        // Interactive Hover State (flat — hover fills can't be a moving gradient)

    // 3. Borders & Focus
    '--border-color': '#276B9B',    // Subtle Dividers (flat — border-color can't hold a gradient)
    '--border-focus': '#F59E0B',    // Tiger Gold Focus Ring
    
    // 4. Typography
    '--text-primary': '#F8FAFC',    // Crisp Text
    '--text-secondary': '#C0CAD9',  // Supporting Text
    '--text-muted': '#8A97AC',      // Placeholders & Disabled
    
    // 5. Accents
    '--accent-blue': '#F59E0B',     // Tiger Gold (Primary Action)
    '--on-accent-blue': '#0F172A',  // Label color on the gold button (RMI-2026-761)
    '--accent-primary': '#A78BFA',  // Tiger Purple, lightened for dark-bg contrast (RMI-2026-325: brand guideline confirms purple #824AF8 is core identity, not decorative -- keep purple in both modes instead of swapping hue to blue)
    '--accent-courier': '#F37664',  // Tiger Coral (RMI-2026-325: brand identity color, was green -- coral previously had zero presence in the app)
    '--accent-tech': '#3B82F6',     // Blue
    '--accent-tech-text': '#93C5FD', // Lighter blue for text on dark blue surfaces (RMI-2026-764)
    '--accent-danger': '#EF4444',   // Red — errors, critical, incomplete
    '--accent-warning': '#F59E0B',  // Amber — delays, caution
    '--accent-success': '#10B981',  // Green — completed, online
    '--tint-primary': 'rgba(167,139,250,0.12)',   // Purple tint bg (matches accent-primary, RMI-2026-325)
    '--tint-primary-border': 'rgba(167,139,250,0.28)',
    '--tint-danger': 'rgba(239,68,68,0.12)',     // Red tint bg
    '--tint-danger-border': 'rgba(239,68,68,0.30)',
    '--tint-warning': 'rgba(245,158,11,0.12)',   // Amber tint bg
    '--tint-tech': 'rgba(59,130,246,0.12)',      // Blue tint bg
    '--tint-success': 'rgba(16,185,129,0.12)',    // Green tint bg
    '--tint-success-border': 'rgba(16,185,129,0.28)',
    '--tint-tech-border': 'rgba(59,130,246,0.28)',
    '--tint-warning-border': 'rgba(245,158,11,0.28)',
    '--accent-secondary': '#F59E0B',              // Tiger Gold — secondary action/highlight
    
    // Form & Layout Scaling 
    '--input-padding': '8px 12px',
    '--form-gap': '0.75rem',
    '--border-radius': '6px',
    '--font-main': "'Poppins', 'Segoe UI', system-ui, sans-serif", // RMI-2026-325: brand guideline specifies Poppins for external-facing material

    // Form Design Tokens
    '--form-label-size':      '0.72rem',
    '--form-label-weight':    '800',
    '--form-label-spacing':   '0.06em',
    '--form-label-color':     'var(--text-muted)',
    '--form-label-transform': 'uppercase',
    '--form-input-height':    '38px',
    '--form-input-radius':    '8px',
    '--form-input-font':      '0.875rem',
    '--form-input-weight':    '600',
    '--form-field-gap':       '5px',
    '--form-section-gap':     '18px',
    '--form-group-padding':   '18px',
    '--form-group-radius':    '10px',
    '--form-hint-size':       '0.68rem',

    // LOB Badge Colors
    // RMI-2026-470: --bg-card is a blue-to-purple brand gradient
    // (#155079 -> #331579) -- a low-opacity same-hue tint (the original
    // 0.10-0.12 alpha) blends into that gradient instead of reading as a
    // badge ("blue on blue" for Technology, same structural issue for
    // Hybrid's purple against the gradient's purple half). Bg opacity raised
    // substantially and text switched to a near-white tint of the same hue,
    // so contrast holds regardless of exactly where the badge sits on the
    // gradient. Logistics (green) and default (gray) don't share this hue
    // collision with the gradient, left as-is. LIGHT theme's card bg is
    // near-white, not affected -- untouched.
    '--lob-tech-bg':          'rgba(37,99,235,0.38)',
    '--lob-tech-text':        '#EFF6FF',
    '--lob-tech-border':      'rgba(147,197,253,0.65)',
    '--lob-logistics-bg':     'rgba(16,185,129,0.12)',
    '--lob-logistics-text':   '#10B981',
    '--lob-logistics-border': 'rgba(16,185,129,0.30)',
    '--lob-hybrid-bg':        'rgba(130,74,248,0.35)',
    '--lob-hybrid-text':      '#F5F3FF',
    '--lob-hybrid-border':    'rgba(196,166,253,0.65)',
    '--lob-default-bg':       '#2A2A2A',
    '--lob-default-text':     '#94A3B8',
    '--lob-default-border':   '#2A2A2A'
  },
  LIGHT: {
    // 1. Layered Backgrounds (RMI-2026-423: same brand-gradient treatment as
    // DARK, just re-lightened to keep the soft/anti-glare/crisp character
    // this theme already had — every value below is a very light, low-to-
    // moderate-saturation derivative of the same three brand hues (Blue
    // #5899CD, Purple #824AF8, Coral #F37664). Floor gets the full
    // blue→purple→coral sweep; navbar/sidebar/input stay blue-led like DARK.
    // MUST use the `background` shorthand, never `background-color` (fixed
    // at every known consumer already, alongside the DARK theme change).
    '--bg-main': 'linear-gradient(135deg, #E0E9F0 0%, #E5E0F0 55%, #F0E2E0 100%)',
    // RMI-2026-568: user "in light mode can we make the white color less
    // bright...it is blinding at times." --bg-sidebar/--bg-card/--bg-input
    // were all ~96-98% luminance -- brighter than --bg-main itself (the page
    // floor, ~90-94%) -- with --bg-input the brightest of all and stacked
    // dozens-deep on every form-heavy screen (WO editor, manifest panel, SRQ
    // forms). Darkened all three ~5-6% while staying inside the same
    // pastel-tinted language --bg-main already established (RMI-2026-423/424)
    // rather than introducing a plain grey.
    '--bg-sidebar': 'linear-gradient(90deg, #EBEFF2 0%, #E1E7ED 100%)',
    '--bg-card': 'linear-gradient(135deg, #ECEEF1 0%, #E4E0EA 100%)',
    '--bg-navbar': 'linear-gradient(90deg, #CFE0EF 0%, #DCCFF3 100%)', // RMI-2026-486: same blue->purple relationship as DARK, scaled light — first attempt (near-white tint of --bg-sidebar) read as too washed out

    // 2. Interactive Elements
    '--bg-input': 'linear-gradient(135deg, #EDEFF1 0%, #E7EAEC 100%)', // RMI-2026-568: was the brightest token in the theme (near-pure-white); darkened to sit closer to --bg-card/--bg-sidebar
    '--bg-hover': '#BCD3E6',        // Interactive Hover State (flat)

    // 3. Borders & Focus
    '--border-color': '#BCD3E6',    // Subtle Dividers (flat)
    '--border-focus': '#3B82F6',    // Tech Blue Focus Ring
    
    // 4. Typography
    '--text-primary': '#0F172A',    // Deep Tiger Navy
    '--text-secondary': '#475569',  // Medium Slate
    '--text-muted': '#94A3B8',      // Placeholders & Disabled
    
    // 5. Accents
    '--accent-blue': '#0F172A',     // Tiger Navy (Primary Action in Light)
    '--on-accent-blue': '#FFFFFF',  // Label color on the navy button (RMI-2026-761)
    '--accent-primary': '#6D28D9',  // Tiger Purple (deeper for light bg)
    '--accent-courier': '#C2604F',  // Deeper Tiger Coral for light contrast (RMI-2026-325: was green)
    '--accent-tech': '#2563EB',     // Deeper blue for light contrast
    '--accent-danger': '#DC2626',   // Red — deeper for light bg
    '--accent-warning': '#D97706',  // Amber — deeper for light bg
    '--accent-success': '#059669',  // Green — deeper for light bg
    '--tint-primary': 'rgba(109,40,217,0.08)',
    '--tint-primary-border': 'rgba(109,40,217,0.18)',
    '--tint-danger': 'rgba(220,38,38,0.08)',
    '--tint-danger-border': 'rgba(220,38,38,0.25)',
    '--tint-warning': 'rgba(217,119,6,0.10)',
    '--tint-tech': 'rgba(37,99,235,0.08)',
    '--tint-success': 'rgba(5,150,105,0.08)',
    '--tint-success-border': 'rgba(5,150,105,0.22)',
    '--tint-tech-border': 'rgba(37,99,235,0.22)',
    '--tint-warning-border': 'rgba(217,119,6,0.22)',
    '--accent-secondary': '#D97706',              // Tiger Gold (deeper for light bg)
    
    // Form & Layout Scaling 
    '--input-padding': '8px 12px',
    '--form-gap': '0.75rem',
    '--border-radius': '6px',
    '--font-main': "'Poppins', 'Segoe UI', system-ui, sans-serif", // RMI-2026-325: brand guideline specifies Poppins for external-facing material

    // Form Design Tokens
    '--form-label-size':      '0.72rem',
    '--form-label-weight':    '800',
    '--form-label-spacing':   '0.06em',
    '--form-label-color':     'var(--text-muted)',
    '--form-label-transform': 'uppercase',
    '--form-input-height':    '38px',
    '--form-input-radius':    '8px',
    '--form-input-font':      '0.875rem',
    '--form-input-weight':    '600',
    '--form-field-gap':       '5px',
    '--form-section-gap':     '18px',
    '--form-group-padding':   '18px',
    '--form-group-radius':    '10px',
    '--form-hint-size':       '0.68rem',

    // LOB Badge Colors
    '--lob-tech-bg':          'rgba(37,99,235,0.08)',
    '--lob-tech-text':        '#2563EB',
    '--lob-tech-border':      'rgba(37,99,235,0.25)',
    '--lob-logistics-bg':     'rgba(5,150,105,0.08)',
    '--lob-logistics-text':   '#059669',
    '--lob-logistics-border': 'rgba(5,150,105,0.25)',
    '--lob-hybrid-bg':        'rgba(109,40,217,0.08)',
    '--lob-hybrid-text':      '#6D28D9',
    '--lob-hybrid-border':    'rgba(109,40,217,0.18)',
    '--lob-default-bg':       '#CBD5E1',
    '--lob-default-text':     '#475569',
    '--lob-default-border':   '#CBD5E1'
  }
};
